import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import api, { API_BASE_URL } from '@/lib/api'

/**
 * White-label brand context. The hostname decides whose platform this is:
 * the provider asks /api/brand once at boot and every branded surface
 * (wordmark, document title, emails-from copy, support links) reads from
 * useBrand() instead of hardcoding a name.
 *
 * The default is the platform's own brand so the app renders correctly
 * before the fetch resolves and on the legacy domain. The last resolved
 * brand is cached in localStorage to avoid a brand flash on reload.
 */

export interface BrandConfig {
  appName: string
  logoUrl: string | null
  faviconUrl: string | null
  primaryColor: string
  supportEmail: string | null
  franchiseId: string | null
  isDefault: boolean
}

// Placeholder neutral brand until the real platform name/domain exist.
// The legacy LegalLady look can be restored on the current domain by
// setting PLATFORM_BRAND_* env vars on the API.
export const DEFAULT_BRAND: BrandConfig = {
  appName: 'Intake.AI',
  logoUrl: '/brand-mark.svg',
  faviconUrl: null,
  primaryColor: '#4d71fd',
  supportEmail: null,
  franchiseId: null,
  isDefault: true,
}

const BRAND_CACHE_KEY = 'platform_brand_cache'

function readCachedBrand(): BrandConfig | null {
  try {
    const raw = localStorage.getItem(BRAND_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.appName !== 'string') return null
    return { ...DEFAULT_BRAND, ...parsed }
  } catch {
    return null
  }
}

const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND)

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<BrandConfig>(() => readCachedBrand() || DEFAULT_BRAND)

  // Resolve the brand to display. Account brand wins over hostname brand:
  // on a shared host (Railway domain — no custom domain or platform
  // subdomain) hostname resolution can only ever return the default brand,
  // so a signed-in franchise admin (or a client they're viewing as) would
  // never see their brand without this authenticated lookup. Falls back to
  // hostname resolution when signed out or when the account has no brand of
  // its own (default/root org).
  const resolveBrand = useCallback(async (): Promise<BrandConfig | null> => {
    let token: string | null = null
    try { token = localStorage.getItem('app_auth_token') } catch { /* storage blocked */ }

    if (token) {
      try {
        const { data } = await api.get('/api/brand/me')
        if (data?.brand && !data.brand.isDefault) {
          return data.brand as BrandConfig
        }
      } catch {
        // not authenticated / network — fall through to hostname brand
      }
    }

    try {
      const host = window.location.hostname
      const res = await fetch(`${API_BASE_URL}/api/brand?host=${encodeURIComponent(host)}`)
      if (res.ok) {
        const data = await res.json()
        if (data?.brand) return data.brand as BrandConfig
      }
    } catch {
      // network failure: keep current/default brand
    }
    return null
  }, [])

  useEffect(() => {
    let cancelled = false
    const run = () => {
      resolveBrand().then(next => {
        if (cancelled || !next) return
        const merged: BrandConfig = { ...DEFAULT_BRAND, ...next }
        setBrand(merged)
        try {
          localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(merged))
        } catch {
          // storage full/blocked — brand still applies for this session
        }
      })
    }
    run()

    // Re-resolve when auth or impersonation changes. Same-tab transitions
    // (login / logout / select-org / view-as) fire a custom event; cross-tab
    // changes arrive through the storage event.
    const onBrandChange = () => run()
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'app_auth_token' || e.key === 'app_impersonation') run()
    }
    window.addEventListener('app:brand-may-have-changed', onBrandChange)
    window.addEventListener('storage', onStorage)
    return () => {
      cancelled = true
      window.removeEventListener('app:brand-may-have-changed', onBrandChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [resolveBrand])

  // Brand side effects: favicon and theme color follow the resolved brand
  useEffect(() => {
    if (brand.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = brand.faviconUrl
    }
  }, [brand.faviconUrl])

  const value = useMemo(() => brand, [brand])
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

export function useBrand(): BrandConfig {
  return useContext(BrandContext)
}

/**
 * Split an app name into [base, styledSuffix] for wordmark styling:
 * "LegalLady.AI" -> ["LegalLady", ".AI"], "Acme Intake" -> ["Acme Intake", ""].
 */
export function splitBrandName(appName: string): [string, string] {
  const match = appName.match(/^(.*?)(\.[A-Za-z]{2,4})$/)
  if (match) return [match[1], match[2]]
  return [appName, '']
}
